import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { deviceType } from 'react-device-detect';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { PAGE_ID_SUCCESS } from 'src/app/constants/pageUid';
import {
  ROUTE_PAY_AND_POST,
  ROUTE_EDIT_PAY_AND_POST,
  ROUTE_REFRESH,
} from 'src/app/constants/routes';
import {
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateThinking } from 'src/app/state/modules/app/thinking';
import { actionUpdateIsJobPostedEventPublished } from 'src/app/state/modules/session/isJobPostedEventPublished';
import { checkPageViewEvent } from 'src/app/utils/analytics/utils/checkPageViewEvent';
import { log } from 'src/app/utils/logging';
import { withDesktopEnhancers } from 'src/app/views/enhancers/withDesktopEnhancers';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import { usePostedJobIdSelector } from 'src/app/views/hooks/usePostedJobIdSelector';
import PUBLISH_JOB_POSTED_EVENT from 'src/graphql/dataPlatformEvents';
import type { PublishJobPostedEventInput } from 'src/graphql/generated';
import type AppState from 'src/types/appState';

import { SuccessBody } from './components/SuccessBody';
import { TrackingForSuggestedApi } from './components/TrackingForSuggestedApi';

export const SuccessPage = () => {
  // If someone tries to navigate back, check where they are going and
  // immediately redirect them to the dashboard if that is better location
  const history = useHistory();
  const { redirectToDashboard } = useLocalisedRoutes();
  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);

  const [shouldNavigateToDashboard, setShouldNavigateToDashboard] =
    useState(false);

  useEffect(() => {
    // Setup an on unmount method so that when someone navigates away from this page
    // it first checks where they are going and if they are going to the previous
    // pay and post pages (of if they are refreshing) then take them to the dashboard instead.
    const onUnmount = shouldNavigateToDashboard
      ? () => redirectToDashboard()
      : history.block(({ pathname }) => {
          if (
            pathname.includes(ROUTE_PAY_AND_POST) ||
            pathname.includes(ROUTE_EDIT_PAY_AND_POST) ||
            pathname.includes(ROUTE_REFRESH)
          ) {
            setShouldNavigateToDashboard(true);
            return false;
          }
        });
    return () => onUnmount();
  });

  const dispatch = useDispatch();
  const {
    cart,
    draftId,
    flowType,
    isGuided,
    isInNotificationsSettingMode,
    isJobPosted,
    isJobPostedEventPublished,
    jobId,
    seekAdvertiserId,
    userId,
  } = useSelector(
    (state: AppState) => ({
      cart: state.session.cart,
      draftId: state.session.draftId,
      flowType: state.formData.flowType,
      isGuided: state.formData.isGuided,
      isInNotificationsSettingMode:
        state.session.notificationsSetting.isInSettingMode,
      isJobPosted: state.session.isJobPosted,
      isJobPostedEventPublished: state.session.isJobPostedEventPublished,
      jobId: state.session.job?.id,
      seekAdvertiserId: state.session.seekAdvertiserId,
      userId: state.session.userId,
    }),
    shallowEqual,
  );

  const postedJobId = usePostedJobIdSelector();

  const [sendJobPublishedEvent] = useMutation<PublishJobPostedEventInput>(
    PUBLISH_JOB_POSTED_EVENT,
    {
      variables: {
        input: {
          adcentreUserId: Number(userId),
          draftId,
          adcentreListingId: Number(postedJobId),
          advertiserId: seekAdvertiserId,
          device: deviceType === 'mobile' ? 'MOBILE' : 'DESKTOP',
          experimentId: '',
          experimentVariantId: '',
          flowType,
          jobId: String(postedJobId),
          jobPostingExperience: 'EXPRESS',
          jobWritingFlow: isGuided ? 'GUIDED' : 'STANDARD',
        },
      },
      onCompleted: () => {
        dispatch(actionUpdateIsJobPostedEventPublished(true));
      },
      onError: (error) => {
        log.error('SuccessPage | SendJobPublishedEvent', {
          error,
          flowType,
          draftId,
          isGuided,
          isInNotificationsSettingMode,
          isJobPosted,
          isJobPostedEventPublished,
          jobId: postedJobId,
          listingId: postedJobId,
          seekAdvertiserId,
          userId,
        });
      },
    },
  );

  useEffect(() => {
    if (!isJobPostedEventPublished && isJobPosted && postedJobId) {
      dispatch(actionUpdateThinking(false));

      if (!isPerUnitBilling) {
        sendJobPublishedEvent();
      }
      if (flowType !== 'refresh' && flowType !== 'edit') {
        checkPageViewEvent(postedJobId);
      }
    }
  }, [
    dispatch,
    flowType,
    isPerUnitBilling,
    isJobPosted,
    isJobPostedEventPublished,
    postedJobId,
    sendJobPublishedEvent,
  ]);

  useEffect(() => {
    if (flowType === 'create' && postedJobId !== jobId) {
      log.error('SuccessPage | Tracking incorrect jobId issue', {
        postedJobId,
        jobId,
        cart,
      });
    }
  }, [cart, flowType, jobId, postedJobId]);

  return isJobPosted ? (
    <>
      {flowType === 'create' && <TrackingForSuggestedApi />}
      <SuccessBody
        jobId={postedJobId}
        inNotificationSettings={isInNotificationsSettingMode}
      />
    </>
  ) : null;
};

export default withDesktopEnhancers(PAGE_ID_SUCCESS)(SuccessPage);
